// pages/UploadPage.js
import { useState, useRef, useEffect, useCallback } from 'react'
import {
  getStorage,
  ref,
  uploadBytes,
  listAll,
  getMetadata,
  deleteObject
} from 'firebase/storage'
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import DeviceSelector from '../components/DecviceSelector'
import './UploadPage.css'

// MUI Icons
import BuildIcon from '@mui/icons-material/Build'
import DescriptionIcon from '@mui/icons-material/Description'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ArticleIcon from '@mui/icons-material/Article'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import TimerIcon from '@mui/icons-material/Timer'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import TimelineIcon from '@mui/icons-material/Timeline'
import HistoryIcon from '@mui/icons-material/History'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import CloseIcon from '@mui/icons-material/Close'
import BarChartIcon from '@mui/icons-material/BarChart'
import InfoIcon from '@mui/icons-material/Info'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import FolderIcon from '@mui/icons-material/Folder'
import DeleteIcon from '@mui/icons-material/Delete'
import StorageIcon from '@mui/icons-material/Storage'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'

function UploadPage () {
  const { user } = useAuth()
  const [uploadState, setUploadState] = useState({
    file: null,
    deviceName: '',
    uploading: false,
    error: null,
    success: null
  })

  // 🆕 Active Job State
  const [activeJob, setActiveJob] = useState(null)
  const [jobHistory, setJobHistory] = useState([])

  // 📁 PDF Storage State
  const [pdfsByDevice, setPdfsByDevice] = useState({})
  const [loadingPdfs, setLoadingPdfs] = useState(true)

  const fileInputRef = useRef(null)
  const db = getFirestore(app)
  const [isDragging, setIsDragging] = useState(false)

  // 🆕 Real-time listener for active jobs
  useEffect(() => {
    if (!user?.userData?.companyId) return

    const companyId = user.userData.companyId

    // Listen for active/processing jobs
    const activeJobQuery = query(
      collection(db, 'processing_jobs'),
      where('companyId', '==', companyId),
      where('status', 'in', ['processing', 'queued']),
      orderBy('createdAt', 'desc'),
      limit(1)
    )

    const unsubscribeActive = onSnapshot(activeJobQuery, snapshot => {
      if (!snapshot.empty) {
        const jobDoc = snapshot.docs[0]
        const jobData = { id: jobDoc.id, ...jobDoc.data() }
        setActiveJob(jobData)
        console.log('📊 Active job detected:', jobData)
      } else {
        setActiveJob(null)
      }
    })

    // Listen for recent completed/failed jobs (last 5)
    const historyQuery = query(
      collection(db, 'processing_jobs'),
      where('companyId', '==', companyId),
      where('status', 'in', ['completed', 'failed']),
      orderBy('completedAt', 'desc'),
      limit(5)
    )

    const unsubscribeHistory = onSnapshot(historyQuery, snapshot => {
      const history = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      setJobHistory(history)
    })

    return () => {
      unsubscribeActive()
      unsubscribeHistory()
    }
  }, [user?.userData?.companyId, db])

  // 📁 Load PDFs from Storage
  const loadPdfs = useCallback(async () => {
    if (!user?.userData?.companyId) return

    setLoadingPdfs(true)
    try {
      const storage = getStorage(app)
      const companyId = user.userData.companyId
      const manualsRef = ref(storage, `manuals/${companyId}`)

      // List all files in the company's manuals folder
      const result = await listAll(manualsRef)

      // Group PDFs by device
      const pdfsGrouped = {}

      // Process each device folder
      for (const deviceRef of result.prefixes) {
        const deviceName = deviceRef.name
        const deviceFiles = await listAll(deviceRef)

        // Get metadata for each file
        const filesWithMetadata = await Promise.all(
          deviceFiles.items.map(async fileRef => {
            try {
              const metadata = await getMetadata(fileRef)
              return {
                name: fileRef.name,
                fullPath: fileRef.fullPath,
                size: metadata.size,
                timeCreated: metadata.timeCreated,
                updated: metadata.updated,
                contentType: metadata.contentType
              }
            } catch (error) {
              console.error('Error fetching metadata:', error)
              return {
                name: fileRef.name,
                fullPath: fileRef.fullPath,
                size: null,
                timeCreated: null,
                updated: null,
                contentType: null
              }
            }
          })
        )

        if (filesWithMetadata.length > 0) {
          pdfsGrouped[deviceName] = filesWithMetadata
        }
      }

      setPdfsByDevice(pdfsGrouped)
    } catch (error) {
      console.error('Error loading PDFs:', error)
    } finally {
      setLoadingPdfs(false)
    }
  }, [user?.userData?.companyId])

  useEffect(() => {
    loadPdfs()
  }, [loadPdfs])

  const handleFileChange = e => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setUploadState(prev => ({
          ...prev,
          error: 'Nur PDF-Dateien sind erlaubt.',
          file: null
        }))
        return
      }
      setUploadState(prev => ({
        ...prev,
        file: selectedFile,
        error: null,
        success: null
      }))
    }
  }

  // 🎨 Drag & Drop Handlers
  const handleDragEnter = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = e => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (uploadState.uploading || activeJob) return

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      if (droppedFile.type !== 'application/pdf') {
        setUploadState(prev => ({
          ...prev,
          error: 'Nur PDF-Dateien sind erlaubt.',
          file: null
        }))
        return
      }
      setUploadState(prev => ({
        ...prev,
        file: droppedFile,
        error: null,
        success: null
      }))
    }
  }

  const handleUpload = async () => {
    const { file, deviceName } = uploadState

    if (!file || !deviceName.trim()) {
      setUploadState(prev => ({
        ...prev,
        error: 'Bitte wählen Sie eine Datei und ein Gerät aus.'
      }))
      return
    }

    if (!user?.userData?.companyId) {
      setUploadState(prev => ({
        ...prev,
        error: 'Company ID nicht gefunden. Bitte melden Sie sich erneut an.'
      }))
      return
    }

    // 🆕 Check for active job
    if (activeJob) {
      setUploadState(prev => ({
        ...prev,
        error: `Ein Upload läuft bereits (${activeJob.fileName}). Bitte warten Sie bis die Verarbeitung abgeschlossen ist.`
      }))
      return
    }

    setUploadState(prev => ({ ...prev, uploading: true, error: null }))

    try {
      const storage = getStorage(app)
      const companyId = user.userData.companyId
      const storageRef = ref(
        storage,
        `manuals/${companyId}/${deviceName.trim()}/${file.name}`
      )
      await uploadBytes(storageRef, file)

      setUploadState({
        file: null,
        deviceName: '',
        uploading: false,
        error: null,
        success: `✅ Datei "${file.name}" erfolgreich hochgeladen! Die Verarbeitung beginnt automatisch.`
      })

      if (fileInputRef.current) {
        fileInputRef.current.value = null
      }

      // Success-Message nach 5 Sekunden ausblenden
      setTimeout(() => {
        setUploadState(prev => ({ ...prev, success: null }))
      }, 5000)
    } catch (error) {
      console.error('Upload-Fehler:', error)
      setUploadState(prev => ({
        ...prev,
        uploading: false,
        error: 'Upload fehlgeschlagen. Bitte versuchen Sie es erneut.'
      }))
    }
  }

  const resetUpload = () => {
    setUploadState({
      file: null,
      deviceName: '',
      uploading: false,
      error: null,
      success: null
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = null
    }
  }

  // 🗑️ Delete PDF
  const handleDeletePdf = async (fullPath, deviceName, fileName) => {
    const confirmDelete = window.confirm(
      `Möchten Sie "${fileName}" wirklich löschen?\n\nDies wird:\n- Die Datei aus dem Cloud Storage entfernen\n- Alle Embeddings aus Pinecone löschen\n- Alle zugehörigen Processing Jobs entfernen\n\nDieser Vorgang kann nicht rückgängig gemacht werden.`
    )

    if (!confirmDelete) return

    try {
      // Call Cloud Function to delete PDF (using Firebase Callable)
      const functions = getFunctions(app, 'us-central1')
      const deletePdfManual = httpsCallable(functions, 'deletePdfManual')

      const result = await deletePdfManual({
        filePath: fullPath,
        deviceName: deviceName
      })

      if (result.data.success) {
        const details = result.data.details
        const detailsText = details
          ? ` (${details.pineconeVectorsDeleted || 0} Embeddings gelöscht)`
          : ''

        setUploadState(prev => ({
          ...prev,
          success: `✅ "${fileName}" wurde erfolgreich gelöscht!${detailsText}`,
          error: null
        }))

        // Reload PDFs to refresh the list and avoid stale data
        await loadPdfs()

        // Hide success message after 5 seconds
        setTimeout(() => {
          setUploadState(prev => ({ ...prev, success: null }))
        }, 5000)
      } else {
        throw new Error('Löschen fehlgeschlagen')
      }
    } catch (error) {
      console.error('Error deleting PDF:', error)
      const errorMessage = error.message || 'Unbekannter Fehler'
      setUploadState(prev => ({
        ...prev,
        error: `Fehler beim Löschen: ${errorMessage}`,
        success: null
      }))
    }
  }

  // 📊 Format file size
  const formatFileSize = bytes => {
    if (!bytes) return 'N/A'
    return (bytes / 1024 / 1024).toFixed(2) + ' MB'
  }

  // 📅 Format date
  const formatDate = dateString => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // 🆕 Helper: Stage Label Translation
  const getStageLabel = stage => {
    const labels = {
      initializing: 'Initialisierung...',
      extracting_content: 'Inhalt wird extrahiert',
      quality_control: 'Qualitätskontrolle',
      generating_embeddings: 'Embeddings werden generiert',
      uploading_batch: 'Upload zu Pinecone',
      completed: 'Abgeschlossen',
      failed: 'Fehler'
    }
    return labels[stage] || stage
  }

  // 🆕 Helper: Format Timestamp
  const formatTimestamp = timestamp => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className='upload-page'>
      <div className='upload-header'>
        <h1>Service Manual hochladen</h1>
        <p>Laden Sie PDF-Handbücher hoch, um die Knowledge-Base zu erweitern</p>
      </div>

      {uploadState.success && (
        <div className='success-banner'>
          <CheckCircleIcon />
          {uploadState.success}
        </div>
      )}

      {uploadState.error && (
        <div className='error-banner'>
          <CancelIcon />
          {uploadState.error}
        </div>
      )}

      {/* 🆕 Active Job Progress Display */}
      {activeJob && (
        <div className='active-job-banner'>
          <div className='job-header'>
            <h3>
              <BarChartIcon className='inline-icon' /> Verarbeitung läuft...
            </h3>
            <span className='job-status'>{activeJob.status}</span>
          </div>

          <div className='job-details'>
            <p>
              <strong>Datei:</strong> {activeJob.fileName}
            </p>
            <p>
              <strong>Gerät:</strong> {activeJob.deviceName}
            </p>
            <p>
              <strong>Phase:</strong> {getStageLabel(activeJob.progress?.stage)}
            </p>
          </div>

          <div className='progress-bar-container'>
            <div
              className='progress-bar-fill'
              style={{ width: `${activeJob.progress?.percentage || 0}%` }}
            />
            <span className='progress-percentage'>
              {activeJob.progress?.percentage || 0}%
            </span>
          </div>

          {activeJob.progress?.currentPage > 0 && (
            <p className='progress-detail'>
              Seite {activeJob.progress.currentPage} von{' '}
              {activeJob.progress.totalPages}
            </p>
          )}

          <p className='job-info'>
            <HourglassEmptyIcon className='inline-icon' />
            Bitte warten Sie - Uploads sind während der Verarbeitung gesperrt
          </p>
        </div>
      )}

      <div className='upload-container'>
        {/* Step Indicators */}
        <div className='steps-indicator'>
          <div
            className={`step ${
              uploadState.deviceName ? 'completed' : 'active'
            }`}
          >
            <div className='step-number'>1</div>
            <div className='step-label'>Gerät wählen</div>
          </div>
          <div className='step-divider' />
          <div
            className={`step ${
              uploadState.file
                ? 'completed'
                : uploadState.deviceName
                ? 'active'
                : ''
            }`}
          >
            <div className='step-number'>2</div>
            <div className='step-label'>PDF hochladen</div>
          </div>
          <div className='step-divider' />
          <div
            className={`step ${
              uploadState.file && uploadState.deviceName ? 'active' : ''
            }`}
          >
            <div className='step-number'>3</div>
            <div className='step-label'>Verarbeiten</div>
          </div>
        </div>

        {/* Step 1: Device Selection */}
        <div className='upload-card'>
          <div className='card-header-upload-page'>
            <div>
              <h3>Gerät auswählen</h3>
              <p className='card-subtitle'>
                Wählen Sie das Gerät für das Manual
              </p>
            </div>
            <div className='card-icon'>
              <BuildIcon />
            </div>
          </div>

          <div className='card-content'>
            <DeviceSelector
              value={uploadState.deviceName}
              onChange={value =>
                setUploadState(prev => ({ ...prev, deviceName: value }))
              }
              disabled={uploadState.uploading || activeJob !== null}
            />

            <p className='input-hint'>
              <LightbulbIcon className='inline-icon' />
              Gerät nicht gefunden?{' '}
              <a href='/admin/devices'>Zur Geräteverwaltung</a>
            </p>
          </div>
        </div>

        {/* Step 2: File Upload */}
        <div className='upload-card'>
          <div className='card-header-upload-page'>
            <div>
              <h3>PDF-Datei hochladen</h3>
              <p className='card-subtitle'>
                Ziehen Sie die Datei hierher oder klicken Sie zum Auswählen
              </p>
            </div>
            <div className='card-icon'>
              <DescriptionIcon />
            </div>
          </div>

          <div className='card-content'>
            <div
              className={`dropzone ${isDragging ? 'dragging' : ''} ${
                uploadState.file ? 'has-file' : ''
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() =>
                !uploadState.uploading &&
                !activeJob &&
                fileInputRef.current?.click()
              }
            >
              <input
                ref={fileInputRef}
                type='file'
                accept='.pdf'
                onChange={handleFileChange}
                disabled={uploadState.uploading || activeJob !== null}
                style={{ display: 'none' }}
              />

              {uploadState.file ? (
                <div className='file-selected'>
                  <div className='file-icon'>
                    <ArticleIcon />
                  </div>
                  <div className='file-info'>
                    <div className='file-name-display'>
                      {uploadState.file.name}
                    </div>
                    <div className='file-size'>
                      {(uploadState.file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                  <button
                    className='file-remove'
                    onClick={e => {
                      e.stopPropagation()
                      resetUpload()
                    }}
                    disabled={uploadState.uploading || activeJob !== null}
                  >
                    <CloseIcon />
                  </button>
                </div>
              ) : (
                <div className='dropzone-empty'>
                  <div className='upload-icon'>
                    <CloudUploadIcon />
                  </div>
                  <p className='dropzone-text'>
                    {isDragging
                      ? 'Datei hier ablegen...'
                      : 'PDF hierher ziehen oder klicken'}
                  </p>
                  <p className='dropzone-hint'>Nur PDF-Dateien, max. 100 MB</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 3: Upload Action */}
        <div className='upload-card'>
          <div className='card-header-upload-page'>
            <div>
              <h3>Verarbeitung starten</h3>
              <p className='card-subtitle'>
                Starten Sie die automatische Extraktion
              </p>
            </div>
            <div className='card-icon'>
              <RocketLaunchIcon />
            </div>
          </div>

          <div className='card-content'>
            <div className='upload-actions-modern'>
              <button
                className='btn-upload'
                onClick={handleUpload}
                disabled={
                  !uploadState.file ||
                  !uploadState.deviceName ||
                  uploadState.uploading ||
                  activeJob !== null
                }
              >
                <FlashOnIcon className='btn-icon' />
                <span>
                  {uploadState.uploading
                    ? 'Lädt hoch...'
                    : activeJob
                    ? 'Upload läuft...'
                    : 'Jetzt hochladen'}
                </span>
              </button>
            </div>

            <div className='info-box'>
              <div className='info-item'>
                <TimerIcon className='info-icon' />
                <span>Dauer: 2-5 Minuten</span>
              </div>
              <div className='info-item'>
                <AutorenewIcon className='info-icon' />
                <span>Automatische Verarbeitung</span>
              </div>
              <div className='info-item'>
                <TimelineIcon className='info-icon' />
                <span>Live-Progress Tracking</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🆕 Job History */}

      {/* 📁 PDF Storage Overview */}
      <div className='pdf-storage-card'>
        <h3>
          <StorageIcon className='inline-icon' /> Gespeicherte Handbücher
        </h3>
        <p className='pdf-storage-subtitle'>
          Alle PDFs die aktuell in Pinecone und Cloud Storage sind
        </p>

        {loadingPdfs ? (
          <div className='pdf-loading'>
            <AutorenewIcon className='loading-spinner' />
            <p>Lade PDFs...</p>
          </div>
        ) : Object.keys(pdfsByDevice).length === 0 ? (
          <div className='pdf-empty'>
            <FolderIcon className='empty-icon' />
            <p>Noch keine Handbücher hochgeladen</p>
          </div>
        ) : (
          <div className='pdf-devices-list'>
            {Object.entries(pdfsByDevice)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([deviceName, pdfs]) => (
                <div key={deviceName} className='pdf-device-group'>
                  <div className='pdf-device-header'>
                    <div className='device-header-content'>
                      <BuildIcon className='device-icon' />
                      <h4>{deviceName}</h4>
                      <span className='device-count'>{pdfs.length} PDF(s)</span>
                    </div>
                  </div>

                  <div className='pdf-files-list'>
                    {pdfs.map(pdf => (
                      <div key={pdf.fullPath} className='pdf-file-item'>
                        <div className='pdf-file-info'>
                          <InsertDriveFileIcon className='file-icon-small' />
                          <div className='pdf-file-details'>
                            <div className='pdf-file-name'>{pdf.name}</div>
                            <div className='pdf-file-meta'>
                              <span>{formatFileSize(pdf.size)}</span>
                              <span>•</span>
                              <span>{formatDate(pdf.timeCreated)}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          className='btn-delete-pdf'
                          onClick={() =>
                            handleDeletePdf(pdf.fullPath, deviceName, pdf.name)
                          }
                          title='PDF löschen'
                        >
                          <DeleteIcon />
                          Löschen
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
      {jobHistory.length > 0 && (
        <div className='job-history-card'>
          <h3>
            <HistoryIcon className='inline-icon' /> Verlauf (letzte 5 Jobs)
          </h3>
          <div className='job-history-list'>
            {jobHistory.map(job => (
              <div key={job.id} className={`job-history-item ${job.status}`}>
                <div className='job-history-header'>
                  <span className='job-history-filename'>{job.fileName}</span>
                  <span className={`job-history-status status-${job.status}`}>
                    {job.status === 'completed' ? (
                      <>
                        <CheckCircleIcon className='status-icon' />{' '}
                        Abgeschlossen
                      </>
                    ) : (
                      <>
                        <CancelIcon className='status-icon' /> Fehler
                      </>
                    )}
                  </span>
                </div>
                <div className='job-history-details'>
                  <span>Gerät: {job.deviceName}</span>
                  <span>{formatTimestamp(job.completedAt)}</span>
                </div>
                {job.status === 'completed' && job.stats && (
                  <div className='job-history-stats'>
                    <span>{job.stats.total_chunks || 0} Chunks</span>
                    <span>{job.stats.total_pages || 0} Seiten</span>
                  </div>
                )}
                {job.status === 'failed' && job.error && (
                  <div className='job-history-error'>
                    <CancelIcon className='inline-icon' />
                    {job.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default UploadPage
